/* eslint-disable unicorn/no-null */
import React, { useState, useEffect, useRef, useMemo } from "react";

import {
  useLiveClassroomData,
  useUpdateImageStreamEnabled,
  useLiveClassroomImages,
} from "@transfr-inc/dashboard-sockets";

import { useContainer } from "@src/contexts/container-context";
import { useApiRequest } from "@lib/hooks";
import { useCurrentUser, useMissionControlEnabled } from "@queries";

import {
  MissionControlContainer,
  MissionControlFullScreenModal,
} from "@views/classrooms/details/components/mission-control";

import { checkObjectIsPopulated } from "@views/classrooms/details/utils/mission-control.utils";

import { trackMissionControl } from "@lib/event-trackers";

export const ClassroomMissionControlTab = ({ classroom, users = [] }) => {
  const [fullScreen, setFullScreen] = useState();
  const socketUrl = process.env.SOCKET_BRIDGE_URL;
  const currentUser = useCurrentUser();
  const missionControlEnabled = useMissionControlEnabled();
  const snowplowedProcessedRef = useRef(false);
  const { token } = currentUser || {};
  const [initSorting, setInitSorting] = useState();
  const [loading, setLoading] = useState(false);
  const spotlightTile = useRef();
  const missionControlDataArray = useRef([]);
  const setSpotlightTile = (tile) => {
    spotlightTile.current = tile;
    if (tile) {
      setFullScreen(true);
    }
  };

  const setMissionControlDataArray = (data) => {
    missionControlDataArray.current = data;
  };

  const timeoutRef = useRef(null);
  const sortRef = useRef(null);
  const usersRef = useRef([]);

  const classroomId = classroom?.classroomId;
  const clientCode = classroom?.clientCode;
  const isStreamingEnabled = !!classroom?.isStreamingEnabled;

  const [showLiveFeed, setShowLiveFeed] = useState(isStreamingEnabled ?? true);

  const { insightsService, classroomService } = useContainer();

  const missionControlClassroom = missionControlEnabled
    ? useLiveClassroomData(socketUrl, token, clientCode, classroomId)
    : {};

  const imageData = useLiveClassroomImages(
    socketUrl,
    token,
    clientCode,
    classroomId
  );

  // Gets mastery for star ratings
  const { response: ceResultsResponse } = useApiRequest(() =>
    insightsService.getClassroomCeResults(classroomId)
  );

  // Convert object map into usable array
  const classroomArray = useMemo(() => {
    const isDataPopulated = checkObjectIsPopulated(missionControlClassroom);
    let data = [];
    if (isDataPopulated) {
      // Reset the ref at the start of each memo computation to prevent duplicates
      usersRef.current = [];
      
      data = Object.entries(missionControlClassroom).map(([userId, info]) => {
        // Skip if we've already processed this userId in this iteration
        if (usersRef.current.includes(userId)) {
          return;
        }
        usersRef.current.push(userId);
   
        const userInfo = users.find((user) => user.userId === userId) || {};
        const hasUserInfo = checkObjectIsPopulated(userInfo);
        if (hasUserInfo) {
          const matchingLearningExperienceResults =
            ceResultsResponse instanceof Map
              ? ceResultsResponse.get(info.learningExperienceId)
              : {};
          const studentResults =
            matchingLearningExperienceResults instanceof Map
              ? matchingLearningExperienceResults.get(userId)
              : {};

          return {
            userInfo,
            userId,
            studentResults,
            ...info,
          };
        } else {
          return {
            userInfo: {
              firstName: "Anonymous",
              lastName: userId.split('-').pop(),
              userId: userId,
              id: userId,
              isAnonymous: true,
            },
            userId,
            studentResults: {},
            ...info,
          };
        }
      });

      data = data.filter((e) => e);

      if (missionControlDataArray.current?.length && initSorting) {
        const prevData = [...missionControlDataArray.current];
        // Update existing items
        const updatedData = prevData.map((existingItem) => {
          const matchingItem = data.find(
            (item) => item.userId === existingItem.userId
          );
          return matchingItem
            ? { ...existingItem, ...matchingItem }
            : existingItem;
        });

        const newItems = data.filter(
          (item) =>
            !prevData.some(
              (existingItem) => existingItem.userId === item.userId
            )
        );
        newItems.forEach((newItem) => {
          trackMissionControl(newItem.userId, classroomId);
        });

        const updatedDataArray = [...updatedData, ...newItems];
        missionControlDataArray.current = updatedDataArray;
      }
    }

    return data;
  }, [missionControlClassroom, ceResultsResponse, users]);

  const { isConnected, sendControlMessage } = useUpdateImageStreamEnabled(
    socketUrl,
    token,
    clientCode,
    classroomId
  );

  const updateLiveFeed = (newShowLiveFeed) => {
    classroomService.updateClassroom(classroomId, {
      isStreamingEnabled: newShowLiveFeed,
    });
  };

  const onToggleLiveFeed = () => {
    setLoading(true);
    setShowLiveFeed((prevShowLiveFeed) => {
      const newShowLiveFeed = !prevShowLiveFeed;
      sendControlMessage({ isStreamingEnabled: newShowLiveFeed });
      updateLiveFeed(newShowLiveFeed);
      return newShowLiveFeed;
    });
    setLoading();
  };

  useEffect(() => {
    return () => {
      setInitSorting();
      setMissionControlDataArray([]);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (sortRef.current) sortRef.current = null;
    };
  }, []);

  useEffect(() => {
    // Update the sorting ref whenever classroomArray changes
    sortRef.current = classroomArray;
  }, [classroomArray]);

  const sortData = () => {
    const sorted = sortRef.current.sort((a, b) => {
      const lastNameA = a?.userInfo?.lastName || "";
      const lastNameB = b?.userInfo?.lastName || "";
      return lastNameA.localeCompare(lastNameB);
    });

    if (!snowplowedProcessedRef.current) {
      sorted.forEach((student) => {
        trackMissionControl(student.userId, classroomId);
      });
      snowplowedProcessedRef.current = true;
    }

    setMissionControlDataArray(sorted);
    setLoading(false);
  };

  useEffect(() => {
    if (classroomArray?.length) {
      if (!initSorting) {
        setLoading(true);
        setInitSorting(true);

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          sortData();
        }, 5000);
      }
    }
  }, [
    initSorting,
    JSON.stringify(classroomArray),
    JSON.stringify(missionControlDataArray.current),
    JSON.stringify(imageData),
  ]);

  const showZero = missionControlDataArray?.current?.length === 0;

  const handleCloseFullScreen = () => {
    setFullScreen();
    setSpotlightTile();
  };

  const MissionControlWrapper = () => (
    <MissionControlContainer
      classroom={classroom}
      users={users}
      fullScreen={fullScreen}
      setFullScreen={setFullScreen}
      loading={loading}
      showZero={showZero}
      missionControlDataArray={missionControlDataArray.current}
      isConnected={isConnected}
      showLiveFeed={showLiveFeed}
      onToggleLiveFeed={onToggleLiveFeed}
      setSpotlightTile={setSpotlightTile}
      spotlightTile={spotlightTile.current}
      imageData={imageData}
      handleCloseFullScreen={handleCloseFullScreen}
    />
  );

  return (
    <>
      {fullScreen ? (
        <MissionControlFullScreenModal
          open={fullScreen}
          onClose={handleCloseFullScreen}
        >
          {MissionControlWrapper()}
        </MissionControlFullScreenModal>
      ) : (
        <>{MissionControlWrapper()}</>
      )}
    </>
  );
};
